import { ArrowBack, CheckCircle, Cancel } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { approveIncidentClosure, getIncidentClosureApproval, rejectIncidentClosure } from '../../api-services/IncidentReportApi';
import { IncidentReport } from '../../helpers/InterfacesServer';
import { formatIncidentStatus } from '../../helpers/IncidentFormatting';
import { showAlertError, showConfirmBox, showInputBox } from '../../helpers/Utils';
import { IncidentManagementDetails } from './IncidentManagementDetails';
import { IncidentSummary } from './IncidentSummary';

interface Props { token: string }

export function IncidentClosureApproval({ token }: Props) {
    const navigate = useNavigate();
    const [report, setReport] = useState<IncidentReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deciding, setDeciding] = useState(false);

    useEffect(() => {
        let active = true;
        getIncidentClosureApproval(token)
            .then(value => { if (active) setReport(value); })
            .catch(() => { if (active) setError('This closure request is unavailable or you are not its assigned CRCO approver.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [token]);

    async function approve() {
        if (!report?.canDecideClosure) return;
        const confirmation = await showConfirmBox('Approve this closure recommendation? The incident will be closed and cannot be edited again.', 'Approve Incident Closure', 'Approve');
        if (!confirmation.isConfirmed) return;
        setDeciding(true);
        try {
            const updated = await approveIncidentClosure(token); setReport(updated);
            enqueueSnackbar('Incident closure approved', { variant: 'success' });
        } finally { setDeciding(false); }
    }

    async function reject() {
        if (!report?.canDecideClosure) return;
        const result = await showInputBox('Rejection comments are required. The incident will return to Incident Management for revision.', 'Reject Incident Closure', 'Enter the reason for rejection', 'Reject');
        if (!result.isConfirmed) return;
        const comments = String(result.value ?? '').trim();
        if (!comments) { await showAlertError('Enter rejection comments before rejecting the closure request.'); return; }
        setDeciding(true);
        try {
            const updated = await rejectIncidentClosure(token, comments); setReport(updated);
            enqueueSnackbar('Closure recommendation rejected', { variant: 'success' });
        } finally { setDeciding(false); }
    }

    if (loading) return <Paper className="Form" sx={{ p: 5, textAlign: 'center' }}><CircularProgress /><Typography>Loading closure request…</Typography></Paper>;
    if (error || !report) return <Paper className="Form" sx={{ p: 3 }}><Alert severity="error">{error || 'Closure request not found.'}</Alert><Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/incident-reporting')}>Back to Incident Reporting</Button></Paper>;

    const latest = report.closureRequests[0];
    return <Paper className="Form" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} mb={2}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/incident-reporting')}>Back to Incident Reporting</Button>
            <Box flexGrow={1} />
            <Typography color="textSecondary">{report.referenceNo} · {formatIncidentStatus(report.status)}</Typography>
        </Stack>
        <Typography variant="h5">Incident Closure Approval</Typography>
        <Typography color="textSecondary" mb={2}>Review the reported incident, assigned actions, and closure recommendation.</Typography>
        {latest?.status === 'APPROVED' && <Alert severity="success" sx={{ mb: 2 }}>This closure request was approved{latest.decisionDate ? ` on ${new Date(latest.decisionDate).toLocaleString()}` : ''}.</Alert>}
        {latest?.status === 'REJECTED' && <Alert severity="error" sx={{ mb: 2 }}>This closure request was rejected. {latest.rejectionComments}</Alert>}
        {report.canDecideClosure && <Paper variant="outlined" sx={{ p: 2, mb: 2 }}><Typography fontWeight="bold">CRCO decision</Typography><Typography color="textSecondary" mb={2}>Approval closes the incident. Rejection requires comments and returns it for revision.</Typography><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={approve} disabled={deciding}>Approve closure</Button><Button variant="outlined" color="error" startIcon={<Cancel />} onClick={reject} disabled={deciding}>Reject closure</Button></Stack></Paper>}
        <IncidentManagementDetails report={report} onChanged={setReport} readOnly />
        <IncidentSummary report={report} showTimeline />
    </Paper>;
}
